// app/src/lib/scale/wages.js — client wrapper for the public /v1/wages route.
//
// Returns the local BLS Leisure & Hospitality wage/labor benchmark for a ZIP:
// avg hourly earnings, avg weekly hours, and employment, each with a 12-month
// % change. Unauthenticated (BLS data is public-domain); the access token is
// forwarded when available, purely for future rate-limit keying.

import { getConfig } from '../config.js';
import { getAccessToken } from '../auth/cognitoClient.js';

/**
 * @param {{ zip:string }} q
 * @returns {Promise<object>} { zip, st, area, stateName, areaName, industry,
 *   earnings:{usd,yoyPct,level}|null, weeklyHours:{hours,yoyPct,level}|null,
 *   employment:{thousands,yoyPct,level}|null, asOf, fetchedAt }
 * @throws {Error} with `.code` 'bad_zip' | 'not_available'
 */
export async function fetchWages({ zip } = {}) {
  if (!/^\d{5}$/.test(String(zip || ''))) {
    const e = new Error('zip must be 5 digits'); e.code = 'bad_zip'; throw e;
  }
  const cfg = getConfig();
  const at = await getAccessToken().catch(() => null);
  const res = await fetch(`${cfg.apiBaseUrl}/v1/wages?zip=${encodeURIComponent(zip)}`, {
    headers: at ? { authorization: `Bearer ${at}` } : {},
  });
  if (res.status === 404) {
    const body = await res.json().catch(() => ({}));
    const e = new Error(body.error || 'no benchmark for this area');
    e.code = 'not_available';
    throw e;
  }
  if (!res.ok) { const e = new Error(`wages ${res.status}`); e.status = res.status; throw e; }
  return res.json();
}
